import React, { useState, useEffect } from "react";
import {
    Modal,
    Box,
    Button,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Chip,
    InputBase,
    CircularProgress,
    Typography,
} from "@mui/material";
import IconList from "../../../data/account/socialMediaIcons";
import { CommunicationType } from "../../../data/CommunicationType";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSave: (type: Partial<CommunicationType>) => Promise<void>; // ✅ Ensures API call is awaited
    existingCommunicationTypes: CommunicationType[];
    selectedCommunication?: CommunicationType | null; // Null means Add mode, otherwise Edit mode
}

const CommunicationTypeModal: React.FC<Props> = ({
                                                     isOpen,
                                                     onClose,
                                                     onSave,
                                                     existingCommunicationTypes,
                                                     selectedCommunication,
                                                 }) => {
    const [comType, setComType] = useState("");
    const [icon, setIcon] = useState("");
    const [tags, setTags] = useState<string[]>([]);
    const [newTag, setNewTag] = useState("");
    const [loading, setLoading] = useState(false); // ✅ Tracks API call state
    const [error, setError] = useState<string | null>(null); // ✅ Handles API errors

    // Determine if it's edit mode
    const isEditMode = !!selectedCommunication;

    // Load data if editing
    useEffect(() => {
        if (isOpen && isEditMode && selectedCommunication) {
            setComType(selectedCommunication.comType);
            setIcon(selectedCommunication.icon);
            setTags(selectedCommunication.tags || []);
        }
        if (!isOpen) {
            handleReset();
        }
    }, [isOpen, selectedCommunication]);

    const handleSave = async () => {
        if (!comType || !icon) {
            setError("Both name and icon are required!");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const updatedType: Partial<CommunicationType> = {
                comType,
                icon,
                tags,
                ...(isEditMode && { comTypeId: selectedCommunication?.comTypeId }),
            };

            await onSave(updatedType);
            onClose();
        } catch (err: any) {
            console.error("❌ API error:", err);
            setError(err.message || "Failed to save. Please try again.");
        } finally {
            setLoading(false);
        }
    };


    const handleAddTag = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && newTag.trim()) {
            event.preventDefault();
            if (!tags.includes(newTag.trim())) {
                setTags([...tags, newTag.trim()]);
                setNewTag("");
            } else {
                setError("This tag already exists!");
            }
        }
    };

    const handleDeleteTag = (tagToDelete: string) => {
        setTags(tags.filter((tag) => tag !== tagToDelete));
    };

    const handleReset = () => {
        setComType("");
        setIcon("");
        setTags([]);
        setNewTag("");
        setError(null);
    };

    return (
        <Modal open={isOpen} onClose={onClose}>
            <Box
                sx={{
                    padding: 3,
                    backgroundColor: "white",
                    margin: "auto",
                    marginTop: "10%",
                    width: 400,
                    borderRadius: 2,
                }}
            >
                <Typography variant="h6" gutterBottom>
                    {isEditMode ? "Edit Communication Type" : "Add Communication Type"}
                </Typography>

                {error && (
                    <Typography color="error" sx={{ marginBottom: 2 }}>
                        {error}
                    </Typography>
                )}

                <InputBase
                    fullWidth
                    placeholder="Name"
                    value={comType}
                    onChange={(e) => setComType(e.target.value)}
                    sx={{
                        marginBottom: 2,
                        padding: 1,
                        border: "1px solid #ddd",
                        borderRadius: 1,
                    }}
                />
                <FormControl fullWidth margin="normal">
                    <InputLabel id="icon-select-label">Icon</InputLabel>
                    <Select
                        labelId="icon-select-label"
                        value={icon}
                        onChange={(e) => setIcon(e.target.value)}
                        label="Icon"
                    >
                        {Object.entries(IconList).map(([key, IconComponent]) => (
                            <MenuItem key={key} value={key}>
                                <span
                                    style={{
                                        marginRight: "10px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {IconComponent && <IconComponent />}
                                </span>
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Tags Input */}
                <Box
                    sx={{
                        marginTop: 2,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: 1,
                        maxHeight: "150px",
                        overflowY: "auto",
                    }}
                >
                    {tags.map((tag) => (
                        <Chip
                            key={tag}
                            label={tag}
                            onDelete={() => handleDeleteTag(tag)}
                            sx={{
                                backgroundColor: "#2196F3",
                                color: "white",
                                "& .MuiChip-label": {
                                    color: "white !important",
                                    fontWeight: "bold",
                                },
                                "& .MuiChip-deleteIcon": {
                                    color: "white",
                                },
                            }}
                        />
                    ))}
                    <InputBase
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyDown={handleAddTag}
                        placeholder="Type and press Enter"
                        sx={{
                            flex: 1,
                            minWidth: "150px",
                            border: "none",
                            outline: "none",
                            "&::placeholder": {
                                color: "#888",
                            },
                        }}
                    />
                </Box>

                {/* Buttons */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                    <Button
                        variant="contained"
                        onClick={onClose}
                        disabled={loading}
                        sx={{
                            marginRight: 2,
                            backgroundColor: "#F44336 !important",
                            color: "white !important",
                            "&:hover": {
                                backgroundColor: "#D32F2F !important",
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading} // ✅ Prevents multiple clicks
                        sx={{
                            backgroundColor: "#4CAF50 !important",
                            color: "white !important",
                            "&:hover": {
                                backgroundColor: "#45A049 !important",
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : isEditMode ? "Save" : "Add"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default CommunicationTypeModal;